/**
 * Diff utilities for benchmark environment snapshot comparison.
 *
 * @module config-comparison/diff-utils
 */

export type DiffStatus = 'changed' | 'added' | 'removed' | 'unchanged';

export interface DiffRow {
  key: string;
  valueA: unknown;
  valueB: unknown;
  status: DiffStatus;
}

/**
 * Diff two flat key/value snapshots, returning rows sorted with changes first.
 */
export function diffSnapshots(
  snapshotA: Record<string, unknown> | null | undefined,
  snapshotB: Record<string, unknown> | null | undefined,
): DiffRow[] {
  const a = snapshotA ?? {};
  const b = snapshotB ?? {};
  const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const rows: DiffRow[] = [];

  for (const key of Array.from(allKeys).sort()) {
    const inA = key in a;
    const inB = key in b;
    if (inA && inB) {
      const same = JSON.stringify(a[key]) === JSON.stringify(b[key]);
      rows.push({ key, valueA: a[key], valueB: b[key], status: same ? 'unchanged' : 'changed' });
    } else if (inA) {
      rows.push({ key, valueA: a[key], valueB: undefined, status: 'removed' });
    } else {
      rows.push({ key, valueA: undefined, valueB: b[key], status: 'added' });
    }
  }

  return rows;
}

export function formatValue(val: unknown): string {
  if (val === undefined || val === null) return '—';
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  if (typeof val === 'number') return String(val);
  if (typeof val === 'string') return val;
  return JSON.stringify(val);
}

/** Convert a typed object to Record<string, unknown> for use with diffSnapshots */
export function toRecord(obj: object | null | undefined): Record<string, unknown> {
  if (!obj) return {};
  return Object.fromEntries(Object.entries(obj));
}

/**
 * Diff two keyed record maps with custom value formatting and equality.
 * Produces DiffRow[] suitable for rendering in a DiffTable.
 */
export function diffRecordMap<T>(
  a: Record<string, T> | null | undefined,
  b: Record<string, T> | null | undefined,
  opts: {
    format: (val: T) => string;
    isEqual: (valA: T, valB: T) => boolean;
    keyLabel?: (key: string, val: T | undefined) => string;
  },
): DiffRow[] {
  const mapA = a ?? {};
  const mapB = b ?? {};
  const allKeys = Array.from(new Set([...Object.keys(mapA), ...Object.keys(mapB)])).sort();
  const rows: DiffRow[] = [];

  for (const key of allKeys) {
    const valA = mapA[key];
    const valB = mapB[key];
    const label = opts.keyLabel?.(key, valA ?? valB) ?? key;
    const inA = valA !== undefined;
    const inB = valB !== undefined;

    if (inA && inB) {
      rows.push({
        key: label,
        valueA: opts.format(valA),
        valueB: opts.format(valB),
        status: opts.isEqual(valA, valB) ? 'unchanged' : 'changed',
      });
    } else if (inA) {
      rows.push({ key: label, valueA: opts.format(valA), valueB: undefined, status: 'removed' });
    } else if (inB) {
      rows.push({ key: label, valueA: undefined, valueB: opts.format(valB!), status: 'added' });
    }
  }

  return rows;
}

/** Count rows that have changed (added/removed/changed) */
export function countChanges(rows: DiffRow[]): number {
  return rows.filter((r) => r.status !== 'unchanged').length;
}

/**
 * Count changes between two keyed record maps using a custom equality check.
 * Returns { changed, total } for use in diff summary badges.
 */
export function countRecordChanges<T>(
  a: Record<string, T> | undefined,
  b: Record<string, T> | undefined,
  isEqual: (valA: T, valB: T) => boolean,
): { changed: number; total: number } {
  const mapA = a ?? {};
  const mapB = b ?? {};
  const allKeys = new Set([...Object.keys(mapA), ...Object.keys(mapB)]);
  let changed = 0;
  for (const key of allKeys) {
    const va = mapA[key];
    const vb = mapB[key];
    if (va === undefined || vb === undefined || !isEqual(va, vb)) changed++;
  }
  return { changed, total: allKeys.size };
}
